const escape = value => String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
const number = value => Math.max(0, Number(value) || 0).toLocaleString('ru-RU');

export function filterUniverses(items, query) {
    const q = String(query || '').trim().toLocaleLowerCase('ru');
    return items.filter(item => String(item.name).toLocaleLowerCase('ru').includes(q));
}

export function universeRows(items, selected, current, apiBase = '') {
    return items.map(item => {
        // Media comes only from the catalogue proxy, never arbitrary URL schemes.
        const cover = String(item.cover_url || '');
        const src = cover.startsWith('/api/proxy_image?') ? apiBase + cover : '';
        const active = item.name === current;
        const chosen = item.name === selected;
        return `<button type="button" class="universe-row" data-universe="${escape(item.name)}" aria-pressed="${chosen}">
            <span class="universe-cover">${src ? `<img src="${escape(src)}" alt="" loading="lazy" decoding="async">` : `<svg viewBox="0 0 40 40" aria-hidden="true"><circle cx="20" cy="20" r="13"/><ellipse cx="20" cy="20" rx="6" ry="13"/><path d="M7 20h26"/></svg>`}</span>
            <span class="universe-row-copy"><strong>${escape(item.name)}</strong><span>${Number.isFinite(item.total_cards) ? `${number(item.total_cards)} карт · собрано ${number(item.owned_cards)}` : 'Открыть коллекцию'}${active ? '<em>Текущая вселенная</em>' : ''}</span></span>
            <span class="universe-radio" aria-hidden="true">${chosen ? '<svg viewBox="0 0 24 24"><path d="m5 12 4 4 10-10"/></svg>' : ''}</span>
        </button>`;
    }).join('');
}

export function createUniversePicker({ root, api, current, select, apiBase = '' }) {
    const get = id => root.querySelector(`#${id}`);
    const list = get('universe-gate-list'), search = get('universe-search');
    const confirm = get('universe-confirm'), closeButton = get('universe-close');
    const status = get('universe-status'), description = get('universe-description');
    let items = [], selected = '', initial = '', blocking = false, busy = false;
    let loading = false, loadError = false;
    let resolveOpen = null, promise = null, generation = 0, previousFocus = null;
    const oldInert = new Map();

    function render() {
        const filtered = filterUniverses(items, search.value);
        list.innerHTML = loadError ? '<button type="button" class="universe-retry">Повторить загрузку</button>' : universeRows(filtered, selected, initial, apiBase);
        list.querySelectorAll('img').forEach(img => { img.onerror = () => { img.hidden = true; }; });
        list.querySelectorAll('button').forEach(button => { button.disabled = busy; });
        get('universe-selection').textContent = selected || 'Выбери вселенную из списка';
        confirm.disabled = busy || loading || loadError || !selected || selected === initial;
        confirm.textContent = busy ? 'Сохраняем…' : selected === initial && initial ? 'Уже выбрана' : 'Выбрать вселенную';
        search.disabled = busy;
        closeButton.disabled = busy;
        if (loading) status.textContent = 'Загружаем вселенные…';
        else if (loadError) status.textContent = 'Не удалось загрузить вселенные.';
        else if (!filtered.length) status.textContent = search.value ? 'Ничего не найдено. Попробуй другое название.' : 'Вселенные пока не загружены. Попробуй позже.';
        else status.textContent = `Найдено: ${filtered.length}`;
    }

    async function load() {
        const revision = ++generation;
        loading = true;
        loadError = false;
        render();
        list.setAttribute('aria-busy', 'true');
        try {
            const data = await api('/api/universes', { allowStale: false });
            if (revision !== generation || !promise) return;
            items = Array.isArray(data?.list) ? data.list : [];
            loading = false;
            render();
        } catch {
            if (revision !== generation || !promise) return;
            loading = false;
            loadError = true;
            render();
        } finally {
            if (revision === generation) list.removeAttribute('aria-busy');
        }
    }

    function finish(result) {
        generation += 1;
        root.classList.remove('open');
        root.setAttribute('aria-hidden', 'true');
        for (const [node, value] of oldInert) node.inert = value;
        oldInert.clear();
        previousFocus?.focus?.();
        const done = resolveOpen;
        resolveOpen = null;
        promise = null;
        done?.(result);
    }
    function close() { if (promise && !blocking && !busy) finish(null); }
    closeButton.onclick = close;
    root.addEventListener('click', event => { if (event.target === root) close(); });
    search.oninput = render;
    list.onclick = event => {
        if (busy) return;
        if (event.target.closest('.universe-retry')) { void load(); return; }
        const button = event.target.closest('[data-universe]');
        if (!button) return;
        selected = button.dataset.universe;
        const name = selected;
        render();
        [...list.querySelectorAll('button')].find(row => row.dataset.universe === name)?.focus();
    };
    confirm.onclick = async () => {
        if (busy || loading || loadError || !selected || selected === initial) return;
        busy = true;
        render();
        try {
            await select(selected);
            finish(selected);
        } catch (error) {
            busy = false;
            render();
            status.textContent = error?.message || 'Не удалось сохранить выбор. Попробуй ещё раз.';
        } finally { busy = false; }
    };
    root.addEventListener('keydown', event => {
        if (event.key === 'Escape') { event.preventDefault(); close(); }
        if (event.key !== 'Tab') return;
        const focusable = [...root.querySelectorAll('button:not(:disabled),input:not(:disabled)')].filter(node => !node.hidden);
        const first = focusable[0], last = focusable.at(-1);
        if (event.shiftKey && (document.activeElement === first || !focusable.includes(document.activeElement))) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || !focusable.includes(document.activeElement))) { event.preventDefault(); first?.focus(); }
    });
    return {
        close,
        open(options = {}) {
            if (promise) return promise;
            initial = current() || '';
            blocking = Boolean(options.blocking || !initial);
            selected = initial;
            items = [];
            busy = false;
            loading = true;
            loadError = false;
            search.value = '';
            previousFocus = document.activeElement;
            closeButton.hidden = blocking;
            description.textContent = blocking ? 'С чего начнётся твоя коллекция? Выбери мир для первых карт.' : 'Новые карты будут из выбранного мира. Переключиться можно в любой момент.';
            root.classList.add('open');
            root.setAttribute('aria-hidden', 'false');
            // Other app surfaces cannot receive focus while choosing the first universe.
            for (const sibling of root.parentElement.children) {
                if (sibling !== root && !['SCRIPT','STYLE','LINK'].includes(sibling.tagName)) {
                    oldInert.set(sibling, sibling.inert); sibling.inert = true;
                }
            }
            promise = new Promise(resolve => { resolveOpen = resolve; });
            render();
            root.querySelector('[role="dialog"]').focus();
            void load();
            return promise;
        },
    };
}
